import { useState, useEffect } from 'react'
import { TranscriptionConfig, TabType } from './types'
import { DEFAULT_TRANSCRIPTION_CONFIG } from './defaults'
import { TabNavigation } from './TabNavigation'
import { PresetBar } from './PresetBar'
import { BasicTab } from './BasicTab'
import { DrumsTab } from './DrumsTab'
import { MelodyTab } from './MelodyTab'
import { AdvancedTab } from './AdvancedTab'

interface TranscriptionSettingsProps {
  value: TranscriptionConfig
  onChange: (config: TranscriptionConfig) => void
}

const STORAGE_KEY = 'transcriptionConfig'

export function TranscriptionSettings({ value, onChange }: TranscriptionSettingsProps) {
  const [activeTab, setActiveTab] = useState<TabType>('basic')

  // LocalStorage persistence
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(value))
    } catch (error) {
      console.error('Failed to save transcription config to localStorage:', error)
    }
  }, [value])

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      if (saved) {
        const parsed = JSON.parse(saved)
        onChange({ ...DEFAULT_TRANSCRIPTION_CONFIG, ...parsed })
      }
    } catch (error) {
      console.error('Failed to load transcription config from localStorage:', error)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="bg-surface-800/50 border border-white/5 rounded-xl p-4">
      <PresetBar config={value} onPresetSelect={onChange} />
      <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />

      <div className="mt-4">
        {activeTab === 'basic' && <BasicTab config={value} onChange={onChange} />}
        {activeTab === 'drums' && <DrumsTab config={value} onChange={onChange} />}
        {activeTab === 'melody' && <MelodyTab config={value} onChange={onChange} />}
        {activeTab === 'advanced' && <AdvancedTab config={value} onChange={onChange} />}
      </div>
    </div>
  )
}

// Re-export types for convenience
export type { TranscriptionConfig } from './types'
export { DEFAULT_TRANSCRIPTION_CONFIG } from './defaults'
